/* eslint-disable max-lines */
import _ from 'underscore'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import TextAreaWithPreview from 'pages/shared/TextAreaWithPreview'
import AlertIcon from 'pages/shared/Alert/Icon'
import Form from 'pages/shared/Form'
import supportedTags from 'pages/shared/Alert/supportedTags'

import Checkbox from 'components/Checkbox'
import RadioInputs from 'components/RadioInputs'
import LabelledField from 'components/LabelledField'
import TextInput from 'components/TextInput'

import {
  alertCodeRawValue,
  convertToTextareaValue,
  parseIntoDescriptionsAndDetails,
  validateAlertDescription
} from 'helpers/alerts'
import { scrubText } from 'helpers/inputCleaners'

import { useStoreContext } from 'hooks/StoreContext'
import useSearchParams from 'hooks/useSearchParams'

import Api from 'services/api'

import { AlertDescription } from 'types/Alert'

import { useConfirmDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

const defaultAlert = {
  draft: true,
  severity: 'minor'
}

type errors = {
  code?: string
  description?: string
  problems?: string
  possibleCauses?: string
  severity?: string
}

type props = {
  saveMethod: string
  alertDescription?: AlertDescription
  displayDeleteButton?: boolean
}

const AlertDescriptionForm = ({ alertDescription = defaultAlert, displayDeleteButton, saveMethod }: props) => {
  const navigate = useNavigate()
  const query = useSearchParams('q') || ''

  const { alertDescriptions, setAreAlertDescriptionsLoaded } = useStoreContext()

  const [currentAlertDescription, setCurrentAlertDescription] = useState({
    ...alertDescription,
    additionalInfo: convertToTextareaValue(alertDescription.additionalInfo ?? []),
    possibleCauses: convertToTextareaValue(alertDescription.possibleCauses ?? []),
    problems: convertToTextareaValue(alertDescription.problems ?? [])
  })

  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<errors>({})

  const { confirm } = useConfirmDialog()
  const componentType = 'Alert'

  const buildSearchParams = ({ forDelete = false } = {}) => {
    const searchParams = new URLSearchParams()

    if (query) {
      searchParams.set('q', query)
    }

    if (!forDelete && currentAlertDescription?.code) {
      searchParams.set('id', alertCodeRawValue(currentAlertDescription.code))
      searchParams.set('showModal', 'true')
    }

    return searchParams.toString().length ? `?${searchParams.toString()}` : ''
  }

  const onSaveCallback = () => {
    setIsSaving(true)
    setErrors({})

    const alertWithBackendCodeValue = {
      ...currentAlertDescription,
      additionalInfo: currentAlertDescription.additionalInfo ? parseIntoDescriptionsAndDetails(currentAlertDescription.additionalInfo) : [],
      possibleCauses: parseIntoDescriptionsAndDetails(currentAlertDescription.possibleCauses),
      problems: parseIntoDescriptionsAndDetails(currentAlertDescription.problems),
      code: alertCodeRawValue(currentAlertDescription.code)
    }

    const validationErrors = validateAlertDescription(alertWithBackendCodeValue, alertDescriptions)

    if (_.isEmpty(validationErrors)) {
      Api.AlertDescriptions[saveMethod](alertWithBackendCodeValue)
        .then(() => {
          setAreAlertDescriptionsLoaded(false) // triggers refetching of alert descriptions

          navigate(`/alerts${buildSearchParams()}`)
        })
        .catch((apiError) => {
          setErrors(apiError || {})
        })
        .finally(() => {
          setIsSaving(false)
        })
    } else {
      setErrors(validationErrors)
      setIsSaving(false)
    }
  }

  const onDeleteCallback = async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
      description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
    })

    if (isConfirmed) {
      Api.AlertDescriptions.delete(
        alertCodeRawValue(currentAlertDescription.code)
      )
        .then(() => {
          setAreAlertDescriptionsLoaded(false) // triggers refetching of alert descriptions

          navigate(`/alerts${buildSearchParams({ forDelete: true })}`)
        })
        .catch((apiError) => {
          setErrors(apiError || {})
        })
    }
  }

  const onCancelCallback = async () => {
    const parseAlertDescription: AlertDescription = {
      ...currentAlertDescription,
      additionalInfo: parseIntoDescriptionsAndDetails(currentAlertDescription.additionalInfo),
      possibleCauses: parseIntoDescriptionsAndDetails(currentAlertDescription.possibleCauses),
      problems: parseIntoDescriptionsAndDetails(currentAlertDescription.problems)
    }

    const hasChanges = !_.isEqual(parseAlertDescription, alertDescription)

    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })

    if (!hasChanges || (hasChanges && isConfirmed)) {
      navigate(`/alerts${buildSearchParams()}`)
    }
  }

  return (
    <Form
      deleteButtonText="Alert"
      fullScreen
      {...{
        isSaving,
        displayDeleteButton,
        onSaveCallback,
        onDeleteCallback,
        onCancelCallback
      }}
    >
      <Checkbox
        label="Draft"
        checked={currentAlertDescription.draft}
        onChange={() => {
          setCurrentAlertDescription(oldDescription => ({
            ...oldDescription,
            draft: !oldDescription.draft
          }))
        }}
      />

      <S.TextInput
        label="Code"
        onChange={(event) => {
          setCurrentAlertDescription(oldDescription => ({
            ...oldDescription,
            code: scrubText(event.target.value)
          }))
        }}
        value={currentAlertDescription.code}
        errorMessage={errors.code}
        disabled={saveMethod === 'update'}
        isRequired
      />
      {saveMethod === 'update' && (
        <S.HelperText>To change a code, delete and create a new entry</S.HelperText>
      )}

      <S.Severity
        label="Severity"
        id="severity"
        isRequired
        errorMessage={errors.severity}
      >
        <S.SeverityInputBox>
          <S.AlertIcon severity={currentAlertDescription.severity} />

          <S.RadioInputs
            selectedValue={currentAlertDescription.severity}
            radioOptions={[
              { label: 'Critical', value: 'critical' },
              { label: 'Major', value: 'major' },
              { label: 'Minor', value: 'minor' }
            ]}
            onChange={(event) => {
              setCurrentAlertDescription(oldDescription => ({
                ...oldDescription,
                severity: event.target.value
              }))
            }}
          />
        </S.SeverityInputBox>
      </S.Severity>

      <S.Tags id="tags" label="Tags">
        {_.keys(supportedTags).map((tag) => (
          <S.TagCheckbox
            key={tag}
            label={tag}
            checked={currentAlertDescription.tags?.includes(tag)}
            onChange={() => {
              setCurrentAlertDescription(oldDescription => {
                const newTags = oldDescription.tags?.includes(tag)
                  ? oldDescription.tags?.filter(t => t !== tag)
                  : [...(oldDescription.tags || []), tag]

                return { ...oldDescription, tags: _.intersection(newTags, _.keys(supportedTags)) }
              })
            }}
          />
        ))}
      </S.Tags>

      <S.TextInput
        label="Description"
        onChange={(event) => {
          setCurrentAlertDescription(oldDescription => ({
            ...oldDescription,
            description: scrubText(event.target.value)
          }))
        }}
        value={currentAlertDescription.description}
        errorMessage={errors.description}
        isRequired
      />

      <S.TextInput
        label="Short Text"
        onChange={(event) => {
          setCurrentAlertDescription(oldShortText => ({
            ...oldShortText,
            shortText: scrubText(event.target.value)
          }))
        }}
        value={currentAlertDescription.shortText}
      />

      <S.TextInput
        label="Notes"
        onChange={(event) => {
          setCurrentAlertDescription(oldNotes => ({
            ...oldNotes,
            notes: scrubText(event.target.value)
          }))
        }}
        value={currentAlertDescription.notes}
      />

      <S.TextAreaWithPreview
        label="Problem Description(s)"
        value={currentAlertDescription.problems ?? ''}
        onChange={event => {
          setCurrentAlertDescription(oldDescription => ({
            ...oldDescription,
            problems: scrubText(event.target.value)
          }))
        }}
        errorMessage={errors.problems}
        isRequired
      />

      <S.TextAreaWithPreview
        label="Possible Cause(s)"
        value={currentAlertDescription.possibleCauses ?? ''}
        onChange={event => {
          setCurrentAlertDescription(oldDescription => ({
            ...oldDescription,
            possibleCauses: scrubText(event.target.value)
          }))
        }}
        errorMessage={errors.possibleCauses}
        isRequired
      />

      <S.TextAreaWithPreview
        label="Additional Information"
        value={currentAlertDescription.additionalInfo ?? ''}
        onChange={event => {
          setCurrentAlertDescription(oldDescription => ({
            ...oldDescription,
            additionalInfo: scrubText(event.target.value)
          }))
        }}
      />
    </Form>
  )
}

export default AlertDescriptionForm

const S = {
  Tags: styled(LabelledField)`
    display: flex;
    gap: 15px;
  `,
  TagCheckbox: styled(Checkbox)`
    margin-top: 2px;
  `,
  TextInput: styled(TextInput)`
    width: 50%;
  `,
  Severity: styled(LabelledField)`
    display: flex;
    width: 50%;
    gap: 4px;
  `,
  SeverityInputBox: styled.div`
    display: flex;
  `,
  AlertIcon: styled(AlertIcon)`
    margin-left: 15px;
  `,
  RadioInputs: styled(RadioInputs)`
    display: inline-flex;
    margin-left: 15px;
  `,
  HelperText: styled.div`
    margin-top: -15px;
    margin-bottom: 10px;
  `,
  TextAreaWithPreview: styled(TextAreaWithPreview)`
    margin-bottom: 15px;
    padding-top: 0;
  `
}
